import * as Sentry from '@sentry/tanstackstart-react';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen';

// Keep the DSN / enabled modes / sample rate in sync with the server-side
// equivalents in `web/recruitment/instrument.server.mjs`. They can't be
// shared because the .mjs file is loaded by `node --import` before any of
// our bundled / TS code is available.
const sentryEnabledModes = ['production', 'staging'];

export function getRouter() {
  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: false,
  });

  // Initialize Sentry in the browser only. The server-side SDK is initialized
  // separately by `instrument.server.mjs`, which is loaded via `node --import`
  // before the server bundle runs.
  if (!router.isServer) {
    const browserService = import.meta.env.VITE_SERVICE ?? 'web-recruitment';
    Sentry.init({
      dsn: 'https://e0e4e72f8ec40ba69845cea7be684c13@o4509739805835264.ingest.de.sentry.io/4510114949365840',
      environment: import.meta.env.MODE,
      enabled: sentryEnabledModes.includes(import.meta.env.MODE),
      tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
      ignoreErrors: [
        // Silence noise from Instagram/Facebook in-app browser injected scripts
        "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers')",
        'sendDataToNative',
        'sendPageHideMessage',
        // Silence noise from Brave iOS/wallet extensions
        "TypeError: undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')",
        // Silence noise from transient connection/navigation errors
        'TypeError: Failed to fetch',
        'TypeError: Load failed',
        'ih: Load failed',
        'AbortError: The user aborted a request.',
        'AbortError: The operation was aborted.',
      ],
      initialScope: {
        tags: { service: browserService },
        attributes: { service: browserService },
      },
      integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    });
  }

  return router;
}
// This declaration is added by tanstack start on vite build. To pass type-checking without having to build, we add the declaration here as well.
declare module '@tanstack/react-start' {
  interface Register {
    ssr: true;
    router: Awaited<ReturnType<typeof getRouter>>;
  }
}
